import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Container,
    alpha,
    useTheme,
} from "@mui/material";
import {
    CheckCircle,
    Error as ErrorIcon,
    Home,
    CreditCard,
    Receipt,
} from "@mui/icons-material";

export default function PaymentComplete() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();

    const [status, setStatus] = useState("loading"); // loading, success, error
    const reference = searchParams.get("reference") || searchParams.get("trxref");

    useEffect(() => {
        // Check if we have a reference parameter
        if (reference) {
            // Payment was successful - Paystack redirects here after success
            setStatus("success");

            // Auto-redirect to dashboard after 5 seconds
            const timer = setTimeout(() => {
                navigate("/home");
            }, 5000);

            return () => clearTimeout(timer);
        } else {
            // No reference found - something went wrong
            setStatus("error");
        }
    }, [reference, navigate]);

    const renderContent = () => {
        switch (status) {
            case "loading":
                return (
                    <Box sx={{ textAlign: "center", py: 6 }}>
                        <CircularProgress size={60} />
                        <Typography variant="h6" sx={{ mt: 3 }}>
                            Processing your payment...
                        </Typography>
                    </Box>
                );

            case "success":
                return (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        {/* Success Icon */}
                        <Box
                            sx={{
                                width: 100,
                                height: 100,
                                borderRadius: "50%",
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 3,
                            }}
                        >
                            <CheckCircle sx={{ fontSize: 60, color: "success.main" }} />
                        </Box>

                        <Typography variant="h4" fontWeight={700} gutterBottom color="success.main">
                            Payment Successful!
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
                            Your subscription has been activated. Thank you for your payment!
                        </Typography>

                        {/* Reference Number */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mb: 4,
                                maxWidth: 400,
                                mx: "auto",
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <Receipt color="primary" fontSize="small" />
                                <Typography variant="body2" color="text.secondary">
                                    Reference:
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    {reference}
                                </Typography>
                            </Box>
                        </Paper>

                        {/* Action Buttons */}
                        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                startIcon={<Home />}
                                onClick={() => navigate("/home")}
                                size="large"
                                sx={{ borderRadius: 2 }}
                            >
                                Go to Dashboard
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CreditCard />}
                                onClick={() => navigate("/settings")}
                                size="large"
                                sx={{ borderRadius: 2 }}
                            >
                                Manage Payment Methods
                            </Button>
                        </Box>

                        {/* Auto-redirect notice */}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 4 }}>
                            You will be automatically redirected to the dashboard in 5 seconds...
                        </Typography>
                    </Box>
                );

            case "error":
                return (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        {/* Error Icon */}
                        <Box
                            sx={{
                                width: 100,
                                height: 100,
                                borderRadius: "50%",
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 3,
                            }}
                        >
                            <ErrorIcon sx={{ fontSize: 60, color: "error.main" }} />
                        </Box>

                        <Typography variant="h4" fontWeight={700} gutterBottom color="error.main">
                            Payment Issue
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: "auto" }}>
                            We couldn't verify your payment. If you believe this is an error, please contact support.
                        </Typography>

                        {/* Action Buttons */}
                        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                            <Button
                                variant="contained"
                                startIcon={<Home />}
                                onClick={() => navigate("/home")}
                                size="large"
                                sx={{ borderRadius: 2 }}
                            >
                                Go to Dashboard
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CreditCard />}
                                onClick={() => navigate("/settings")}
                                size="large"
                                sx={{ borderRadius: 2 }}
                            >
                                Try Again
                            </Button>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Container maxWidth="sm" sx={{ py: 8 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                }}
            >
                {renderContent()}
            </Paper>
        </Container>
    );
}
